import { useState, type FormEvent } from 'react';

interface TopicRepliesProps {
  replies: string[];
  onNotify: (message: string) => void;
}

const MAX_LENGTH = 500;

export default function TopicReplies({ replies, onNotify }: TopicRepliesProps) {
  const [list, setList] = useState<string[]>(replies);
  const [draft, setDraft] = useState('');

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = draft.trim();
    if (!value) {
      onNotify('回复内容不能为空');
      return;
    }
    if (value.length > MAX_LENGTH) {
      onNotify(`回复不能超过 ${MAX_LENGTH} 字`);
      return;
    }
    setList((prev) => [value, ...prev]);
    setDraft('');
    onNotify('回复已发布（本地演示，仅当前页面可见）');
  };

  return (
    <section className="mt-10 md:mt-12" aria-label="话题回复">
      <h2 className="flex items-center gap-2 font-heading text-xl md:text-2xl text-foreground-950 mb-5">
        <i className="ri-chat-smile-2-line text-primary-500" />
        参与讨论
        <span className="text-sm font-normal text-foreground-400">（{list.length} 条回复）</span>
      </h2>

      <form onSubmit={handleSubmit} className="p-4 md:p-5 rounded-lg border border-background-200 bg-background-50">
        <textarea
          name="reply"
          value={draft}
          onChange={(event) => setDraft(event.target.value.slice(0, MAX_LENGTH))}
          rows={3}
          maxLength={MAX_LENGTH}
          placeholder="友善发言，聊聊你的看法…"
          className="w-full resize-none rounded-md border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-400"
        />
        <div className="mt-3 flex items-center justify-between gap-3">
          <span className="text-xs text-foreground-400">
            {draft.length} / {MAX_LENGTH} · 回复仅作前端演示，不会真实存储
          </span>
          <button
            type="submit"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 whitespace-nowrap cursor-pointer transition-colors"
          >
            <i className="ri-send-plane-fill" />
            发布回复
          </button>
        </div>
      </form>

      <div className="mt-5 flex flex-col gap-3">
        {list.map((reply, index) => (
          <div key={`${reply}-${index}`} className="flex gap-3 p-4 rounded-lg bg-background-100">
            <span className="w-9 h-9 rounded-full bg-secondary-700 text-background-50 flex items-center justify-center shrink-0 text-sm font-medium">
              友
            </span>
            <div className="min-w-0">
              <p className="text-xs text-foreground-400 mb-1">热心网友 {list.length - index}</p>
              <p className="text-sm text-foreground-800 leading-relaxed break-words">{reply}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}